import { PTAC_TERMINAL_KINDS } from '@alpinehomeair/common';
import type { SizingBandsSettingType, SystemOptionType } from 'data/types';
import { resolveZoneSizingThresholds } from 'utils/compatibility-systems';
import { isPtacSelectedSystem } from 'utils/ptac-selected-system';
import type { PtacUnitSizeMatch } from 'utils/ptac-size-match-types';
import {
  classifyZoneMatch,
  type ZoneMatchResult,
  type ZoneMatchSideRules
} from 'utils/zone-match-labels';

// The Size Match for Hotel Style PTAC systems, the way the ducted detail
// rates its zones: each unit's rated output over its room's design load,
// classified against the admin sizing bands (the heat pump cooling band for
// a PTHP, the AC band for a PTAC, the heating band for heat) or the default
// 90-120% window when the bands have not loaded. A unit switches between its
// heat pump and heater rather than running both, so its heating output is
// already the larger source.

const MATCH_PERCENT_CAP = 200;

const UNKNOWN_MATCH: ZoneMatchResult = { label: '—', variant: 'unknown' };

type PtacZone =
  SystemOptionType['configuration']['condensers'][number]['zones'][number];

function requiredBtu(zone: PtacZone): { heating: number; cooling: number } {
  return zone.zoneItems.reduce(
    (totals, item) => ({
      heating: totals.heating + (item.BTUs?.heating ?? 0),
      cooling: totals.cooling + (item.BTUs?.cooling ?? 0)
    }),
    { heating: 0, cooling: 0 }
  );
}

function matchPercent(capacity: number, required: number): number | null {
  if (required <= 0) return null;
  return Math.min(MATCH_PERCENT_CAP, (capacity / required) * 100);
}

// A side with no load, or a unit with no heating output at all, says nothing
// about fit; the other side carries the label.
function sideRulesFor(
  heatingCapacity: number,
  required: { heating: number; cooling: number }
): ZoneMatchSideRules {
  return {
    heating:
      heatingCapacity > 0 && required.heating > 0 ? 'default' : 'excluded',
    cooling: required.cooling > 0 ? 'default' : 'excluded'
  };
}

// The terminal kind rides the option's tags (PTAC / PTHP), which is what picks
// the cooling band.
function systemTypeLabelOf(system: Pick<SystemOptionType, 'tags'>): string {
  const kind = PTAC_TERMINAL_KINDS.find((entry) =>
    system.tags.includes(entry.label)
  );
  return kind?.systemTypeLabel ?? PTAC_TERMINAL_KINDS[0].systemTypeLabel;
}

export function buildPtacSizeMatches(
  system: SystemOptionType,
  sizingBands: SizingBandsSettingType | null
): PtacUnitSizeMatch[] {
  if (!isPtacSelectedSystem(system)) return [];
  const thresholds =
    resolveZoneSizingThresholds(sizingBands, systemTypeLabelOf(system)) ??
    undefined;
  return system.configuration.condensers.flatMap((condenser) =>
    condenser.zones.map((zone) => {
      const required = requiredBtu(zone);
      const heatingCapacity = zone.selectedProduct?.heatingBTU ?? 0;
      const coolingCapacity = zone.selectedProduct?.coolingBTU ?? 0;
      const heatingMatchPercent = matchPercent(
        heatingCapacity,
        required.heating
      );
      const coolingMatchPercent = matchPercent(
        coolingCapacity,
        required.cooling
      );
      const match =
        classifyZoneMatch(
          heatingMatchPercent ?? 100,
          coolingMatchPercent ?? 100,
          thresholds,
          sideRulesFor(heatingCapacity, required)
        ) ?? UNKNOWN_MATCH;
      return {
        condenserUniqueId: condenser.uniqueId,
        zoneUniqueId: zone.uniqueId,
        zoneName: zone.name,
        heatingMatchPercent:
          heatingMatchPercent === null ? null : Math.round(heatingMatchPercent),
        coolingMatchPercent:
          coolingMatchPercent === null ? null : Math.round(coolingMatchPercent),
        match
      };
    })
  );
}
